import React, { useState } from 'react';
import { Badge, Box, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import DirectionsBikeRoundedIcon from '@mui/icons-material/DirectionsBikeRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import OutboxRoundedIcon from '@mui/icons-material/OutboxRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { useNavigate } from 'react-router-dom';
import { STATUS_ACCENTS } from '../../theme/oceanTokens';

const TONES = { red: STATUS_ACCENTS.cancelled, amber: STATUS_ACCENTS.booked, indigo: STATUS_ACCENTS.confirmed };
const ICONS = {
  invoices_overdue: AccessTimeRoundedIcon,
  tpa_queries: HealthAndSafetyRoundedIcon,
  claims_ageing: AccountBalanceRoundedIcon,
  critical_uncalled: PhoneInTalkRoundedIcon,
  approvals_pending: FactCheckRoundedIcon,
  unassigned_visits: DirectionsBikeRoundedIcon,
  stock: Inventory2RoundedIcon,
  qc_failures: ScienceRoundedIcon,
  sendouts_overdue: OutboxRoundedIcon,
};

/**
 * The lab alerts bell (06 §2.4): what needs attention now, computed by the server on request.
 * A red dot when there is anything; each row links to where it is dealt with.
 */
const LabAlertsBell = ({ items = [] }) => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);

  return (
    <>
      <Tooltip title="Alerts">
        <IconButton size="small" aria-label="Lab alerts" onClick={(e) => setAnchor(e.currentTarget)}>
          <Badge variant="dot" color="error" invisible={!items.length}>
            <NotificationsRoundedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ sx: { width: 320, maxWidth: 'calc(100vw - 32px)' } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Typography fontWeight={800} sx={{ px: 2, pt: 1, pb: 1 }}>Alerts</Typography>
        {items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1.5 }}>Nothing needs attention.</Typography>
        )}
        {items.map((item) => {
          const tone = TONES[item.tone] || TONES.amber;
          const Icon = ICONS[item.kind] || ErrorOutlineRoundedIcon;
          return (
            <MenuItem key={item.kind} onClick={() => { setAnchor(null); navigate(item.link); }} sx={{ alignItems: 'flex-start', whiteSpace: 'normal', py: 1.25 }}>
              <Stack direction="row" spacing={1.5} sx={{ minWidth: 0 }}>
                <Box sx={{
                  width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
                  color: tone.from, bgcolor: tone.glow,
                }}>
                  <Icon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700}>{item.title}</Typography>
                  {item.body && <Typography variant="body2" color="text.secondary" noWrap>{item.body}</Typography>}
                  {item.meta && <Typography variant="caption" color="text.secondary">{item.meta}</Typography>}
                </Box>
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default LabAlertsBell;
